import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import AreaShell from '../../chrome/AreaShell';
import { ConfirmDialog, Dialog, Field, Select, Textarea, TextInput } from '../../components/forms';
import { Segmented, TCard, TEmpty, TFoot, Toolbar, TRow, TSkeleton } from '../../components/table';
import { useToast } from '../../components/roster';
import useAnnouncements, { Announcement, AnnouncementType } from '@/src/Announcements/hooks/useAnnouncements';
import './admin-content.css';

/*
 * Platform announcements (release notes and notices). There is no draft or
 * inactive state: every saved announcement shows to every user, so posting
 * and saving both confirm first. useAnnouncements swallows errors and
 * returns null / [], so results are checked by value.
 */

const EMPTY_DRAFT = { title: '', message: '', type: AnnouncementType.Release, by: '' };

type TypeFilter = 'all' | 'release' | 'notice';

const typeLabel = (t: AnnouncementType) => (t === AnnouncementType.Announcement ? 'Notice' : 'Release');
const AUDIENCE = 'It appears for every user of every org as soon as it’s saved.';

export default function AdminAnnouncementsV3() {
    const {
        listAnnouncements,
        createAnnouncement,
        updateAnnouncement,
        deleteAnnouncement,
        error: hookError,
    } = useAnnouncements();
    const toast = useToast();

    const [rows, setRows] = useState<Announcement[] | null>(null);
    const [query, setQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

    const load = useCallback(async () => {
        const data = (await listAnnouncements()) ?? [];
        setRows([...data].sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        load();
    }, [load]);
    /* listAnnouncements returns [] on failure; the hook's error tells them apart */
    const listFailed = hookError === 'Failed to list announcements';

    /* ---- create / edit, confirmed ---- */
    const [dialog, setDialog] = useState<{ kind: 'create' } | { kind: 'edit'; row: Announcement } | null>(null);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [tried, setTried] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const savingRef = useRef(false);

    const openCreate = () => {
        setDraft(EMPTY_DRAFT);
        setTried(false);
        setSaveError('');
        setDialog({ kind: 'create' });
    };
    const openEdit = (row: Announcement) => {
        setDraft({
            title: row.title ?? '',
            message: row.message ?? '',
            type: row.type ?? AnnouncementType.Release,
            by: row.by ?? '',
        });
        setTried(false);
        setSaveError('');
        setDialog({ kind: 'edit', row });
    };

    const titleError = draft.title.trim() ? null : 'Title is required.';
    const messageError = draft.message.trim() ? null : 'Message is required.';

    const review = () => {
        if (saving) return;
        setTried(true);
        if (titleError || messageError) return;
        setConfirming(true);
    };

    const save = async () => {
        if (!dialog || savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setSaveError('');
        const body = {
            title: draft.title.trim(),
            message: draft.message.trim(),
            type: draft.type,
            by: draft.by.trim(),
        };
        const result =
            dialog.kind === 'create' ? await createAnnouncement(body) : await updateAnnouncement(dialog.row.id, body);
        savingRef.current = false;
        setSaving(false);
        if (result) {
            setDialog(null);
            toast.show(dialog.kind === 'create' ? `Posted “${body.title}” to everyone.` : `Saved “${body.title}”.`);
            load();
        } else {
            setSaveError('Couldn’t save. Nothing was posted. Try again.');
        }
    };

    /* ---- delete ---- */
    const [deleting, setDeleting] = useState<Announcement | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const remove = async (row: Announcement) => {
        if (busyId !== null) return;
        setBusyId(row.id);
        const result = await deleteAnnouncement(row.id);
        setBusyId(null);
        if (result !== null) {
            setRows((cur) => cur && cur.filter((r) => r.id !== row.id));
            toast.show(`Deleted “${row.title}”.`);
        } else {
            toast.show(`Couldn’t delete “${row.title}”.`);
        }
    };

    const q = query.trim().toLowerCase();
    const visible = (rows ?? []).filter(
        (r) =>
            (typeFilter === 'all' ||
                (typeFilter === 'notice'
                    ? r.type === AnnouncementType.Announcement
                    : r.type !== AnnouncementType.Announcement)) &&
            (!q ||
                (r.title ?? '').toLowerCase().includes(q) ||
                (r.message ?? '').toLowerCase().includes(q) ||
                (r.by ?? '').toLowerCase().includes(q)),
    );
    const showFailed = listFailed && rows !== null && rows.length === 0;

    return (
        <AreaShell
            area="admin"
            tab="announcements"
            sub="Release notes and notices shown to every user of every org"
            actions={
                <button type="button" className="tb-new" onClick={openCreate}>
                    New announcement
                </button>
            }
        >
            <Toolbar
                searchValue={query}
                onSearch={setQuery}
                searchPlaceholder="Search title, message, or author"
                filterCount={0}
                inline={
                    <Segmented
                        label="Type"
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'release', label: 'Releases' },
                            { value: 'notice', label: 'Notices' },
                        ]}
                        value={typeFilter}
                        onChange={setTypeFilter}
                    />
                }
            />
            {showFailed ? (
                <TCard>
                    <TEmpty message="Couldn’t load announcements." actionLabel="Retry" onAction={load} />
                </TCard>
            ) : rows === null ? (
                <TCard>
                    <TSkeleton rows={4} />
                </TCard>
            ) : visible.length === 0 ? (
                <TCard>
                    <TEmpty
                        message={rows.length === 0 ? 'Nothing posted yet.' : 'No announcements match.'}
                        actionLabel={rows.length === 0 ? 'New announcement' : 'Clear filters'}
                        onAction={
                            rows.length === 0
                                ? openCreate
                                : () => {
                                      setQuery('');
                                      setTypeFilter('all');
                                  }
                        }
                    />
                </TCard>
            ) : (
                <TCard>
                    {visible.map((row, i) => {
                        const posted = row.createdAt ? moment(row.createdAt).format('MMM D, YYYY') : '—';
                        return (
                            <TRow
                                key={row.id}
                                index={i}
                                name={row.title}
                                meta={[row.by, row.message].filter(Boolean).join(' · ')}
                                metaDate={posted}
                                date={posted}
                                status={busyId === row.id ? 'Deleting…' : typeLabel(row.type)}
                                onOpen={() => openEdit(row)}
                                menuItems={[
                                    { label: 'Edit', onClick: () => openEdit(row) },
                                    { label: 'Delete…', onClick: () => setDeleting(row) },
                                ]}
                            />
                        );
                    })}
                </TCard>
            )}
            {rows !== null && !showFailed && (
                <TFoot>
                    {q || typeFilter !== 'all'
                        ? `${visible.length} of ${rows.length} · filtered`
                        : `${rows.length} posted · every one is visible to all users`}
                </TFoot>
            )}

            <Dialog
                open={dialog !== null && !confirming}
                title={dialog?.kind === 'edit' ? 'Edit announcement' : 'New announcement'}
                onClose={() => !saving && setDialog(null)}
                onSubmit={review}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setDialog(null)} disabled={saving}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="tb-new"
                            disabled={saving || (tried && (!!titleError || !!messageError))}
                        >
                            {saving ? 'Saving…' : dialog?.kind === 'edit' ? 'Save…' : 'Post…'}
                        </button>
                    </>
                }
            >
                <p className="adm-scope">{AUDIENCE}</p>
                <Field label="Title" error={tried && titleError ? titleError : undefined}>
                    <TextInput
                        autoFocus
                        value={draft.title}
                        disabled={saving}
                        onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                    />
                </Field>
                <Field label="Message" error={tried && messageError ? messageError : undefined}>
                    <Textarea
                        rows={5}
                        value={draft.message}
                        disabled={saving}
                        spellCheck
                        onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value }))}
                    />
                </Field>
                <div className="two" style={{ marginTop: 14 }}>
                    <Field label="Type">
                        <Select
                            value={draft.type}
                            disabled={saving}
                            onChange={(e) => setDraft((d) => ({ ...d, type: +e.target.value as AnnouncementType }))}
                        >
                            <option value={AnnouncementType.Release}>Release</option>
                            <option value={AnnouncementType.Announcement}>Notice</option>
                        </Select>
                    </Field>
                    <Field label="By">
                        <TextInput
                            placeholder="Your name"
                            autoComplete="name"
                            value={draft.by}
                            disabled={saving}
                            onChange={(e) => setDraft((d) => ({ ...d, by: e.target.value }))}
                        />
                    </Field>
                </div>
                {saveError && (
                    <p className="field-e" role="alert" style={{ marginTop: 10 }}>
                        {saveError}
                    </p>
                )}
            </Dialog>

            <ConfirmDialog
                open={confirming}
                title={dialog?.kind === 'edit' ? `Save “${draft.title.trim()}” for everyone?` : `Post “${draft.title.trim()}” to everyone?`}
                body={AUDIENCE}
                actionLabel={dialog?.kind === 'edit' ? 'Save for everyone' : 'Post to everyone'}
                danger={false}
                onConfirm={save}
                onClose={() => setConfirming(false)}
            />

            <ConfirmDialog
                open={deleting !== null}
                title={deleting ? `Delete “${deleting.title}”?` : ''}
                body="It disappears for every user right away. This can’t be undone."
                actionLabel="Delete"
                onConfirm={() => deleting && remove(deleting)}
                onClose={() => setDeleting(null)}
            />
            {toast.node}
        </AreaShell>
    );
}
